import { Canvas3d } from "./Canvas3d.js";
import { Toolbar } from "../display/Toolbar.js";

export interface OrbitCamera3dOptions {
    canvas: Canvas3d;
    latitude: number;
    longitude: number;
    toolbar: Toolbar;
    radius: number;
}

export class OrbitCamera3d extends e5.Actor<e5.Pointer> {
    public onZoom = e5.Signal.create<[number]>();
    public onChange = e5.Signal.create();

    public minZoom: number = 1;
    public maxZoom: number = 5;

    private _distance: number = 1000;
    private _canvas: Canvas3d;
    private _camera: THREE.PerspectiveCamera;
    private _zoomTarget: number = NaN;
    private _lat: number = 0;
    private _lng: number = 0;
    private _zoomRadiusMultiplier: number = 116.60779645636836;

    constructor(options: OrbitCamera3dOptions) {
        super(null, e5.ActorNonOptions);

        this._canvas = options.canvas;
        this._camera = this._canvas.camera;

        // this.element = this._canvas.foreground;
        this.element = this._canvas.element;

        this._zoomRadiusMultiplier = (this._canvas.computeProjectedRadius(options.radius, this._distance) / this._canvas.camera.zoom);
        this.maxZoomRadius = 1024 + 512;
        this.minZoomRadius = 128;
        this._lng = options.longitude;
        this._lat = options.latitude;
        this.updateCamera();
        this.zoomRadius = 256;

        var mover = new MovePixel(this._canvas, this);

        this.onDown.add(() => {
            gsap.killTweensOf(this);
            mover.start(this.pointer);
        });

        this.onMove.add(() => {
            if (this.pointers.length <= 1) {
                mover.move(this.pointer);
            }
        });

        var clientX = 0;
        var clientY = 0;
        this.element.addEventListener("mousemove", (e) => {
            clientX = e.clientX;
            clientY = e.clientY;
        });

        var handleZoom = (e: any, delta: number) => {
            if (e) {
                try {
                    e.preventDefault();
                } catch (error) {
                }
            }

            if (delta > 0) {
                this.zoomIn();
            } else {
                this.zoomOut();
            }
        };

        this.element.addEventListener("wheel", (e) => handleZoom(e, -e.deltaY), { passive: false });

        var scaling = 0;
        var scalingZoom = 0;
        this.onDown.add(() => {
            if (this.pointers.length > 1) {
                scalingZoom = this.zoom;
                scaling = this.distance;
            }
        });

        this.onMove.add((): void => {
            if (this.pointers.length > 1) {
                this.zoom = scalingZoom * (this.distance / scaling);

                scalingZoom = this.zoom;
                scaling = this.distance;
            }
        });

        this._canvas.foreground.tabIndex = 0;
        this._canvas.foreground.setAttribute("role", "application");

        this._canvas.foreground.addEventListener("keydown", (e) => {

            var speed: number = 10;

            if (e5.KeyCode.is(e, e5.KeyCode.LEFT_ARROW)) {
                this.lng -= speed;
            }
            else if (e5.KeyCode.is(e, e5.KeyCode.RIGHT_ARROW)) {
                this.lng += speed;
            }
            else if (e5.KeyCode.is(e, e5.KeyCode.UP_ARROW)) {
                this.lat += speed;
            }
            else if (e5.KeyCode.is(e, e5.KeyCode.DOWN_ARROW)) {
                this.lat -= speed;
            }
            else if (e5.KeyCode.is(e, e5.KeyCode.ADD)) {
                this.zoomIn();
            }
            else if (e5.KeyCode.is(e, e5.KeyCode.SUBTRACT)) {
                this.zoomOut();
            } else {
                return;
            }

            try {
                e.preventDefault();
            } catch (error) {

            }
        });
    }

    public get lat(): number {
        return this._lat;
    }

    public set lat(value: number) {
        value = this.clampLatitude(value);
        if (this._lat === value) {
            return;
        }
        this._lat = value;
        this.updateCamera();
    }

    public get lng(): number {
        return this._lng;
    }

    public set lng(value: number) {
        value = this.clampLongitude(value);
        if (this._lng === value) {
            return;
        }
        this._lng = value;
        this.updateCamera();
    }

    private clampLongitude(lng: number): number {
        //0 -> 360
        lng = (lng + 180) % 360;
        if (lng < 0) {
            lng += 360;
        }
        if (lng > 360) {
            lng -= 360;
        }
        lng -= 180;
        lng = e5.Calc.clamp(lng, -180, 180);
        return lng;
    }

    private clampLatitude(lat: number): number {
        lat = e5.Calc.clamp(lat, -90, 90);
        return lat;
    }

    public lookAt(lng: number, lat: number, time: number | "auto" = "auto"): void {

        if (time === "auto") {
            time = 1;
        }

        lng = this.clampLongitude(lng);
        lat = this.clampLatitude(lat);

        if (this._lat === lat
            && this._lng === lng) {
            return;
        }

        gsap.to(this,
            {
                duration: time,
                directionalRotation: { _lat: lat, _lng: lng + "_short" },
                onUpdate: () => {
                    this.updateCamera();
                }
            });
    }

    private updateCamera(): void {
        this._lat = e5.Calc.clamp(this._lat, -90, 90);
        this._canvas.lngLatToVector(this._lng, this._lat, this._distance, this._canvas.camera.position);
        this._canvas.camera.lookAt(0, 0, 0);
        this.onChange.dispatch();
    }

    public get zoom(): number {
        return this._canvas.camera.zoom;
    }

    public set zoom(value: number) {
        value = e5.Calc.clamp(value, this.minZoom, this.maxZoom);
        if (value === this.zoom) {
            return;
        }
        this._camera.zoom = value;
        this._camera.updateProjectionMatrix();
        this.onZoom.dispatch(this.zoom);
    }

    public get zoomRatio(): number {
        var ra = (this.zoom - this.minZoom) / (this.maxZoom - this.minZoom);
        return e5.Calc.clamp(ra, 0, 1);
    }

    public set zoomRatio(value: number) {
        value = e5.Calc.clamp(value, 0, 1);
        this.zoom = this.minZoom + (value * (this.maxZoom - this.minZoom));
    }

    public get zoomRadius(): number {
        return this._camera.zoom * this._zoomRadiusMultiplier;
    }

    public set zoomRadius(value: number) {
        this.zoom = value / this._zoomRadiusMultiplier;
    }

    public get maxZoomRadius(): number {
        return this.maxZoom * this._zoomRadiusMultiplier;
    }

    public set maxZoomRadius(value: number) {
        this.maxZoom = value / this._zoomRadiusMultiplier;
    }

    public get minZoomRadius(): number {
        return this.maxZoom * this._zoomRadiusMultiplier;
    }

    public set minZoomRadius(value: number) {
        this.minZoom = value / this._zoomRadiusMultiplier;
    }

    public get zoomTarget(): number {
        return isNaN(this._zoomTarget) ? this.zoom : this._zoomTarget;
    }

    public setZoom(zoom: number, time: number = 0.25): void {
        zoom = e5.Calc.clamp(zoom, this.minZoom, this.maxZoom);
        if (this.zoom === zoom) {
            return;
        }

        this._zoomTarget = zoom;
        gsap.to(this,
            {
                duration: time,
                zoom: zoom,
                onInterrupt: () => {
                    this._zoomTarget = NaN;
                },
                onComplete: () => {
                    this._zoomTarget = NaN;
                }
            });
    }

    public setZoomRatio(ratio: number, time: number = 0.25): void {
        ratio = e5.Calc.clamp(ratio, 0, 1);

        this.setZoom(this.minZoom + (ratio * (this.maxZoom - this.minZoom)), time);
    }

    public zoomIn(time: number = 0.25): void {
        // this.setZoomRatio(this.zoomRatio + 0.2, time);
        this.setZoom(this.zoomTarget * (2), time);
    }

    public zoomOut(time: number = 0.25): void {
        // this.setZoomRatio(this.zoomRatio - 0.2, time);
        this.setZoom(this.zoomTarget * (1 / 2), time);
    }
}

class MovePixel {

    private source: THREE.Vector3;
    private target: THREE.Vector3;

    private _reverseX: boolean = false;

    constructor(
        private _canvas: Canvas3d,
        private _orbit: OrbitCamera3d) {
    }

    private updateSource(pointer: e5.Pointer): void {
        this.source = this._canvas.getVector(pointer.x, pointer.y);
        this._reverseX = this.clampClientPosition(pointer.x, pointer.y)
        if (!this.source) {
            return;
        }
        this.source.normalize();
    }

    start(pointer: e5.Pointer): void {
        this.updateSource(pointer);
    }

    move(pointer: e5.Pointer): void {

        this._canvas.camera.updateMatrixWorld(true);
        this.target = this._canvas.getVector(pointer.x, pointer.y);
        if (!this.target || !this.source) {
            return;
        }
        this.target.normalize();

        var lat: number = this._orbit.lat;
        var lng: number = this._orbit.lng;

        var radiusPixel = 45 / this._canvas.computeProjectedRadius();

        var speed = 0.125;
        speed = radiusPixel;

        if (this._reverseX) {
            lng += pointer.deltaX * speed;
        } else {
            lng -= pointer.deltaX * speed;
        }

        lat += pointer.deltaY * speed;
        lat = e5.Calc.clamp(lat, -90, 90);

        this._orbit.lookAt(lng, lat, 0);
    }

    end(pointer: e5.Pointer): void {

    }

    private clampClientPosition(clientX: number, clientY: number): boolean {
        var north = new THREE.Vector3(0, 500, 0);
        var radius = this._canvas.camera.position.length();
        var camPos = this._canvas.camera.position.clone();
        if (camPos.distanceTo(north) < radius) {
            var screen = this._canvas.local3dToGlobal2d(north);
            if (clientY < screen.y + 50) {
                return true;
            }
        } else {
            var south = new THREE.Vector3(0, -500, 0);
            var screen = this._canvas.local3dToGlobal2d(south);

            if (clientY > screen.y - 50) {
                return true;
            }

        }

        return false;
    }
}